<!--
  ~ Copyright 2026 NXGN Management, LLC. All Rights Reserved.
  -->

<div id="initiateApplication">
  <h1 class="mb-5 mt-2 pl-2">Initiate Merchant Application</h1>
  <div class="row">
    <div class="col-md-10">
      <form [formGroup]="applicationForm" class="needs-validation form-horizontal" novalidate (ngSubmit)="submit()">
        <fieldset class="mb-3">
          <div class="col-md-12">
            <legend class="row ml-sm-0 mb-3 borderDivide">
              <div class="borderSeparation">NextGen Configuration</div>
            </legend>
          </div>
          <div class="offset-md-3 form-group fieldIsRequired">
            <label class="col-sm-8 control-label" for="processorType"> Processor Type </label>
            <div class="col-md-8">
              <select class="form-control" id="processorType" formControlName="processorType">
                <option *ngFor="let processor of processorTypes" [value]="processor">{{ processor }}</option>
              </select>
            </div>
          </div>
          <div class="offset-md-3 form-group fieldIsRequired">
            <label class="col-sm-8 control-label" for="softwareName"> Client Type </label>
            <div class="col-md-8 position-relative">
              <select
                class="form-control"
                id="softwareName"
                formControlName="softwareName"
                [attr.disabled]="profilesLoading ? '' : null"
                [ngClass]="{ 'is-invalid': form.softwareName.invalid && form.softwareName.touched }"
              >
                <option value="">Select a client type</option>
                <option *ngFor="let name of softwareNames" [value]="name">{{ name }}</option>
              </select>
              <span *ngIf="profilesLoading" class="spinner-border spinner-border-sm" style="position: absolute; right: 2.25rem; top: 0.65rem; pointer-events: none;" role="status" aria-hidden="true"></span>
              <div
                class="formValidationMessage"
                role="alert"
                *ngIf="form.softwareName.invalid && form.softwareName.touched"
              >
                <div class="mt-1">Field is required.</div>
              </div>
            </div>
          </div>
          <div class="offset-md-3 form-group fieldIsRequired">
            <label class="col-sm-8 control-label" for="pricingProfile"> Pricing Profile </label>
            <div class="col-md-8 position-relative">
              <select
                class="form-control"
                id="pricingProfile"
                formControlName="pricingProfile"
                [attr.disabled]="profilesLoading ? '' : null"
                [ngClass]="{ 'is-invalid': form.pricingProfile.invalid && form.pricingProfile.touched }"
              >
                <option value="">Select a pricing profile</option>
                <option *ngFor="let profile of pricingProfiles" [value]="profile">{{ profile }}</option>
              </select>
              <span *ngIf="profilesLoading" class="spinner-border spinner-border-sm" style="position: absolute; right: 2.25rem; top: 0.65rem; pointer-events: none;" role="status" aria-hidden="true"></span>
              <div
                class="formValidationMessage"
                role="alert"
                *ngIf="form.pricingProfile.invalid && form.pricingProfile.touched"
              >
                <div class="mt-1">Field is required.</div>
              </div>
            </div>
          </div>
        </fieldset>

        <fieldset class="mb-3">
          <div class="col-md-12">
            <legend class="row ml-sm-0 mb-3 borderDivide">
              <div class="borderSeparation">Company Information</div>
            </legend>
          </div>
          <div class="offset-md-3 form-group fieldIsRequired">
            <label class="col-sm-8 control-label" for="legalName"> Legal Company Name </label>
            <div class="col-md-8">
              <input
                type="text"
                class="form-control"
                id="legalName"
                formControlName="legalName"
                placeholder="e.g., Acme Corporation"
                [ngClass]="{ 'is-invalid': form.legalName.invalid && form.legalName.touched }"
              />
              <div class="formValidationMessage" role="alert" *ngIf="form.legalName.invalid && form.legalName.touched">
                <div class="mt-1">Field is required.</div>
              </div>
            </div>
          </div>
        </fieldset>

        <fieldset class="mb-3">
          <div class="col-md-12">
            <legend class="row ml-sm-0 mb-3 borderDivide">
              <div class="borderSeparation">Primary Contact</div>
            </legend>
          </div>
          <div class="offset-md-3 form-group fieldIsRequired">
            <label class="col-sm-8 control-label" for="firstName"> First Name </label>
            <div class="col-md-8">
              <input
                type="text"
                class="form-control"
                id="firstName"
                formControlName="firstName"
                placeholder="First name"
                [ngClass]="{ 'is-invalid': form.firstName.invalid && form.firstName.touched }"
              />
              <div class="formValidationMessage" role="alert" *ngIf="form.firstName.invalid && form.firstName.touched">
                <div class="mt-1">Field is required.</div>
              </div>
            </div>
          </div>
          <div class="offset-md-3 form-group fieldIsRequired">
            <label class="col-sm-8 control-label" for="lastName"> Last Name </label>
            <div class="col-md-8">
              <input
                type="text"
                class="form-control"
                id="lastName"
                formControlName="lastName"
                placeholder="Last name"
                [ngClass]="{ 'is-invalid': form.lastName.invalid && form.lastName.touched }"
              />
              <div class="formValidationMessage" role="alert" *ngIf="form.lastName.invalid && form.lastName.touched">
                <div class="mt-1">Field is required.</div>
              </div>
            </div>
          </div>
          <div class="offset-md-3 form-group fieldIsRequired">
            <label class="col-sm-8 control-label" for="email"> Email Address </label>
            <div class="col-md-8">
              <input
                type="email"
                class="form-control"
                id="email"
                formControlName="email"
                placeholder="contact@example.com"
                [ngClass]="{ 'is-invalid': form.email.invalid && form.email.touched }"
              />
              <div class="formValidationMessage" role="alert" *ngIf="form.email.invalid && form.email.touched">
                <div class="mt-1" *ngIf="form.email.errors?.required">Field is required.</div>
                <div class="mt-1" *ngIf="form.email.errors?.email">Enter a valid email address.</div>
              </div>
            </div>
          </div>
        </fieldset>

        <fieldset class="mb-3">
          <div class="col-md-12">
            <legend class="row ml-sm-0 mb-3 borderDivide d-flex justify-content-between align-items-center">
              <div class="borderSeparation">Merchant Locations</div>
              <div class="d-flex align-items-center">
                <small class="text-muted mr-3" id="maxLocationsMessage" *ngIf="!canAddLocation">Maximum of {{ maxLocations }} locations</small>
                <button type="button" class="btn btn-primary btn-sm" id="addLocation" [disabled]="!canAddLocation" (click)="addLocation()">
                  <span>
                    <fa-icon [icon]="faPlus" size="sm"></fa-icon>
                  </span>
                  Add Location
                </button>
              </div>
            </legend>
          </div>
          <div formArrayName="locations">
            <div
              class="offset-md-3 col-md-8 border rounded p-3 mb-3 location-item"
              *ngFor="let location of locations.controls; let i = index"
              [formGroupName]="i"
            >
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h6 class="mb-0">Location {{ i + 1 }}</h6>
                <button
                  type="button"
                  class="btn btn-link text-danger p-0 removeLocation"
                  [disabled]="locations.length === 1"
                  (click)="removeLocation(i)"
                >
                  <span>
                    <fa-icon [icon]="faTrash" size="sm"></fa-icon>
                  </span>
                  Remove
                </button>
              </div>
              <div class="form-group fieldIsRequired">
                <label class="control-label" [for]="'dbaName' + i"> DBA (Doing Business As) </label>
                <input
                  type="text"
                  class="form-control"
                  [id]="'dbaName' + i"
                  formControlName="dbaName"
                  placeholder="Business name as it operates"
                  [ngClass]="{
                    'is-invalid': location.get('dbaName')?.invalid && location.get('dbaName')?.touched
                  }"
                />
                <div
                  class="formValidationMessage"
                  role="alert"
                  *ngIf="location.get('dbaName')?.invalid && location.get('dbaName')?.touched"
                >
                  <div class="mt-1">Field is required.</div>
                </div>
              </div>
            </div>
          </div>
        </fieldset>

        <div class="offset-md-3">
          <div class="col-md-8">
            <button
              type="submit"
              class="btn btn-primary mr-3"
              id="submitApplication"
              [disabled]="applicationForm.invalid || actionInProgress"
            >
              Submit Application
              <span
                *ngIf="actionInProgress"
                class="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              ></span>
            </button>
            <button type="button" class="btn btn-secondary" id="cancelApplication" (click)="cancel()">Cancel</button>
          </div>
        </div>
      </form>
    </div>
  </div>
</div>
